import { Children, isValidElement, PropsWithChildren, ReactNode } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Steps as AntdSteps, StepProps } from 'antd';

import { StepsContextProvider, useStepsContext } from './contexts';
import { StepModel } from '../../hooks';
import { Convert } from '../Convert';

interface Props<T> {
  stepModel: T;
  stepDefinitions?: StepProps[];
  className?: string;
}

const StepsBase = <T,>({
  stepModel,
  stepDefinitions,
  children,
  className,
}: PropsWithChildren<Props<T>>) => {
  const model = stepModel as StepModel<[undefined]>;
  const childrenArray = Children.toArray(children);

  return (
    <StepsContextProvider stepModel={model}>
      <Flex
        direction="column"
        className={className}
        style={{
          width: '100%',
        }}
      >
        <Convert
          value={stepDefinitions}
          expression={(stepDefinitions) => (
            <AntdSteps size="small" items={stepDefinitions} current={model.step} />
          )}
        />
        {childrenArray.map((child) => {
          if (!isValidElement(child) || child.props.step !== model.step) {
            return null;
          }
          return child;
        })}
      </Flex>
    </StepsContextProvider>
  );
};

const StepsItem = <const T extends [unknown, ...unknown[]], S extends keyof T = 0>({
  children,
}: {
  step: S;
  children: (model: T[S]) => ReactNode;
}) => {
  const context = useStepsContext<StepModel<[undefined]>>();
  return children(context.model as T[S]);
};

export const Steps = Object.assign(StepsBase, {
  Item: StepsItem,
});
